import { create } from 'zustand';
import { Signal } from '@/api/types';

interface SignalsState {
  signals: Signal[];
  currentPrices: Record<string, number>; // signalId -> currentPrice
  isLoading: boolean;
  error: string | null;

  // Actions
  setSignals: (signals: Signal[]) => void;
  addSignal: (signal: Signal) => void;
  updateSignal: (id: string, updates: Partial<Signal>) => void;
  removeSignal: (id: string) => void;
  updateCurrentPrice: (signalId: string, price: number) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearSignals: () => void;
}

export const useSignalsStore = create<SignalsState>((set) => ({
  signals: [],
  currentPrices: {},
  isLoading: false,
  error: null,

  setSignals: (signals: Signal[]) =>
    set({
      signals,
      error: null,
    }),

  addSignal: (signal: Signal) =>
    set((state) => ({
      signals: [signal, ...state.signals],
      error: null,
    })),

  updateSignal: (id: string, updates: Partial<Signal>) =>
    set((state) => ({
      signals: state.signals.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      error: null,
    })),

  removeSignal: (id: string) =>
    set((state) => ({
      signals: state.signals.filter((s) => s.id !== id),
      currentPrices: Object.fromEntries(
        Object.entries(state.currentPrices).filter(([key]) => key !== id)
      ),
    })),

  updateCurrentPrice: (signalId: string, price: number) =>
    set((state) => ({
      currentPrices: { ...state.currentPrices, [signalId]: price },
    })),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  setError: (error: string | null) => set({ error }),

  clearSignals: () =>
    set({
      signals: [],
      currentPrices: {},
      error: null,
    }),
}));
